import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  DestroyRef,
  Directive,
  effect,
  EffectRef,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  Signal,
  ViewContainerRef,
} from '@angular/core';
import { RIGHT_SIDENAV_STATE } from '@qtek/shared/services';
import { QtBadgeComponent } from '@qtek/shared/components';
import { PERMISSION_STATUS } from '../../models';

const BADGE_POSITIONS: Record<string, ConnectedPosition> = {
  TOP_RIGHT: {
    originX: 'end',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
  },
  TOP_LEFT: {
    originX: 'start',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
  },
};

@Directive({
  selector: '[qtPermissionBadge]',
  standalone: true,
})
export class PermissionBadgeDirective implements OnInit, OnDestroy {
  private injectedStatus = inject<Signal<string | undefined>>(
    PERMISSION_STATUS,
    { optional: true }
  );

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly overlay = inject(Overlay);
  private readonly rightSidenavState = inject(RIGHT_SIDENAV_STATE, {
    optional: true,
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly renderer = inject(Renderer2);

  /** Manual badge status (optional). If present, overrides DI value. */
  @Input('qtPermissionBadge')
  set inputStatus(value: string | undefined) {
    const status =
      typeof value === 'string' && value !== '' ? value : undefined;
    this.inputStatusSignal.set(status);
  }

  @Input('qtPermissionBadgePosition')
  set position(value: 'top-right' | 'top-left' | '') {
    if (value === '' || value === undefined || value === null) {
      this.positionSignal.set('top-right');
      return;
    }
    this.positionSignal.set(value);
  }

  private readonly inputStatusSignal = signal<string | undefined>(undefined);
  private readonly positionSignal = signal<'top-right' | 'top-left'>(
    'top-right'
  );

  private overlayRef: OverlayRef | null = null;
  private compRef?: ComponentRef<QtBadgeComponent>;
  private io?: IntersectionObserver;
  private ro?: ResizeObserver;
  private parentRos: ResizeObserver[] = [];
  private layoutWrapperMutation?: MutationObserver;
  private positionCheckRaf?: number;
  private tabHeaderCleanup: (() => void) | null = null;
  private lastPosition?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  private effectRef!: EffectRef;
  private positionEffectRef!: EffectRef;
  private observersInitialized = false;

  constructor() {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el.nativeElement)
      .withPositions([BADGE_POSITIONS['TOP_RIGHT']])
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'feature-badge-overlay-panel',
    });

    this.effectRef = effect(() => {
      const status = this.getCurrentStatus();
      this.handleStatusChange(status);
    });

    this.positionEffectRef = effect(() => {
      const pos = this.positionSignal();
      if (this.isAttached()) {
        this.updateOverlayPosition(pos);
      }
    });

    const rightSidenavState = this.rightSidenavState;
    if (rightSidenavState) {
      effect(() => {
        rightSidenavState.opened(); // track signal
        const runReposition = () => {
          if (!this.isAttached() || !this.overlayRef) return;
          this.updateOverlayPosition(this.positionSignal());
        };
        [0, 80, 200, 350, 500].forEach(delay =>
          setTimeout(runReposition, delay)
        );
      });
    }
  }

  ngOnInit(): void {
    // Observers will be initialized lazily when status becomes available
    const initialStatus = this.getCurrentStatus();
    if (initialStatus) {
      this.initializeObservers();
    }
  }

  /**
   * Get current status from input or injected source.
   * Manual input takes precedence over injected status.
   */
  private getCurrentStatus(): string | undefined {
    const manual = this.inputStatusSignal();
    const injected = this.injectedStatus?.() ?? undefined;
    return manual !== undefined
      ? manual
      : injected !== undefined
        ? injected
        : undefined;
  }

  /**
   * Handle status changes - attach/detach badge and manage observers.
   */
  private handleStatusChange(status: string | undefined): void {
    if (status) {
      if (!this.observersInitialized) {
        this.initializeObservers();
      }
      this.attachOrUpdateBadge(status);
    } else {
      this.detachBadge();
      // Keep observers active in case status returns quickly
      // They'll be cleaned up on destroy
    }
  }

  /**
   * Initialize all observers for position tracking.
   * Only called when badge has a status to display.
   */
  private initializeObservers(): void {
    if (this.observersInitialized) return;
    this.observersInitialized = true;

    this.setupIntersectionObserver();
    this.setupResizeObservers();
    this.setupTabHeaderListeners();
    this.observeLayoutWrapper();
    this.startPositionTracking();
  }

  /** Reposition overlay when right sidenav opens/closes (layout__wrapper margin-right changes). */
  private observeLayoutWrapper(): void {
    let el: HTMLElement | null = this.el.nativeElement;
    for (let i = 0; i < 25 && el; i++) {
      if (el.classList?.contains('layout__wrapper')) {
        const runReposition = () => {
          if (!this.isAttached() || !this.overlayRef) return;
          this.updateOverlayPosition(this.positionSignal());
        };
        const scheduleUpdate = () => {
          [0, 50, 150, 300, 450].forEach(delay =>
            setTimeout(runReposition, delay)
          );
        };
        this.layoutWrapperMutation = new MutationObserver(scheduleUpdate);
        this.layoutWrapperMutation.observe(el, {
          attributes: true,
          attributeFilter: ['style'],
        });
        break;
      }
      el = el.parentElement;
    }
  }

  /** Reposition overlay when tab header scrolls (chevron click). */
  private setupTabHeaderListeners(): void {
    let el: HTMLElement | null = this.el.nativeElement;
    let tabHeader: HTMLElement | null = null;
    for (let i = 0; i < 15 && el; i++) {
      if (
        el.classList?.contains('mat-mdc-tab-header') ||
        el.classList?.contains('mat-tab-header')
      ) {
        tabHeader = el;
        break;
      }
      el = el.parentElement;
    }
    if (!tabHeader) return;

    const runReposition = () => {
      if (!this.isAttached() || !this.overlayRef) return;
      this.applyManualPosition();
    };
    const scheduleUpdate = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(runReposition);
      });
      [50, 200, 400].forEach(delay => setTimeout(runReposition, delay));
    };

    const onScroll = () => scheduleUpdate();
    const onTransitionEnd = (e: Event) => {
      if (tabHeader?.contains(e.target as Node)) scheduleUpdate();
    };

    tabHeader.addEventListener('scroll', onScroll, { passive: true });
    tabHeader.addEventListener('transitionend', onTransitionEnd, true);

    const scrollable =
      tabHeader.querySelector(
        '.mat-mdc-tab-header-pagination-scroll, .mat-tab-header-pagination-scroll, [class*="tab-header-pagination"]'
      ) ?? tabHeader;
    if (scrollable !== tabHeader) {
      scrollable.addEventListener('scroll', onScroll, { passive: true });
    }

    this.tabHeaderCleanup = () => {
      tabHeader?.removeEventListener('scroll', onScroll);
      tabHeader?.removeEventListener('transitionend', onTransitionEnd, true);
      if (scrollable !== tabHeader) {
        scrollable.removeEventListener('scroll', onScroll);
      }
      this.tabHeaderCleanup = null;
    };
  }

  /**
   * Setup IntersectionObserver to hide badge when element is not visible.
   */
  private setupIntersectionObserver(): void {
    this.io = new IntersectionObserver(
      entries => {
        const visible = entries.some(
          e => e.isIntersecting && e.intersectionRatio > 0
        );
        if (!visible) {
          this.detachBadge();
        } else {
          // Re-attach if visible and has status
          const status = this.getCurrentStatus();
          if (status) {
            this.attachOrUpdateBadge(status);
          }
        }
      },
      { threshold: 0.01 }
    );
    this.io.observe(this.el.nativeElement);
  }

  /**
   * Setup ResizeObservers on element and parent layout containers.
   */
  private setupResizeObservers(): void {
    const updatePosition = () => {
      if (this.isAttached()) {
        requestAnimationFrame(() => {
          if (this.isAttached() && this.overlayRef) {
            this.overlayRef.updatePosition();
          }
        });
      }
    };

    // Observe element itself
    this.ro = new ResizeObserver(updatePosition);
    this.ro.observe(this.el.nativeElement);

    // Observe parent layout containers (flex/grid)
    this.observeParentContainers(updatePosition);
  }

  /**
   * Walk up DOM tree and observe flex/grid containers that affect layout.
   */
  private observeParentContainers(callback: () => void): void {
    let parent: HTMLElement | null = this.el.nativeElement.parentElement;
    let depth = 0;
    const maxDepth = 5;

    while (parent && depth < maxDepth) {
      // Always observe body/documentElement - check before expensive getComputedStyle
      if (parent === document.body || parent === document.documentElement) {
        const parentRo = new ResizeObserver(callback);
        parentRo.observe(parent);
        this.parentRos.push(parentRo);
        break;
      }

      // Only call getComputedStyle for non-body elements
      const style = window.getComputedStyle(parent);
      const display = style.display;
      const isLayoutContainer =
        display === 'flex' ||
        display === 'grid' ||
        display === 'inline-flex' ||
        display === 'inline-grid';

      if (isLayoutContainer) {
        const parentRo = new ResizeObserver(callback);
        parentRo.observe(parent);
        this.parentRos.push(parentRo);
      }

      parent = parent.parentElement;
      depth++;
    }
  }

  /**
   * Track element position changes using requestAnimationFrame.
   * Also hide badge when it overlaps the right sidenav (so it doesn't appear "outside" the container).
   */
  private startPositionTracking(): void {
    const checkPosition = () => {
      if (!this.isAttached()) {
        this.positionCheckRaf = requestAnimationFrame(checkPosition);
        return;
      }

      const rect = this.el.nativeElement.getBoundingClientRect();
      const currentPosition = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };

      if (this.lastPosition) {
        const positionChanged =
          this.lastPosition.top !== currentPosition.top ||
          this.lastPosition.left !== currentPosition.left;

        if (positionChanged) {
          this.updateOverlayPosition(this.positionSignal());
        }
      }

      this.lastPosition = currentPosition;
      this.updateBadgeVisibilityWhenOverlappingSidenav();
      this.positionCheckRaf = requestAnimationFrame(checkPosition);
    };

    this.positionCheckRaf = requestAnimationFrame(checkPosition);
  }

  private updateBadgeVisibilityWhenOverlappingSidenav(): void {
    if (!this.overlayRef?.hasAttached()) return;
    const pane = this.overlayRef.overlayElement as HTMLElement;
    if (!this.rightSidenavState?.isOpened()) {
      pane.style.visibility = '';
      return;
    }
    const sidenav = document.querySelector(
      '.right-sidenav'
    ) as HTMLElement | null;
    if (!sidenav) {
      pane.style.visibility = '';
      return;
    }
    const paneRect = pane.getBoundingClientRect();
    const sidenavRect = sidenav.getBoundingClientRect();
    if (sidenavRect.width <= 0) {
      pane.style.visibility = '';
      return;
    }
    const overlaps =
      paneRect.right > sidenavRect.left &&
      paneRect.left < sidenavRect.right &&
      paneRect.bottom > sidenavRect.top &&
      paneRect.top < sidenavRect.bottom;
    pane.style.visibility = overlaps ? 'hidden' : '';
  }

  private stopPositionTracking(): void {
    if (this.positionCheckRaf !== undefined) {
      cancelAnimationFrame(this.positionCheckRaf);
      this.positionCheckRaf = undefined;
    }
  }

  private isAttached(): boolean {
    return !!this.overlayRef && this.overlayRef.hasAttached();
  }

  /**
   * Attach the overlay component if needed, and update its status.
   */
  private attachOrUpdateBadge(status: string): void {
    if (!this.overlayRef) return;

    if (!this.overlayRef.hasAttached()) {
      const portal = new ComponentPortal(QtBadgeComponent);
      this.compRef = this.overlayRef.attach(portal);
      requestAnimationFrame(() =>
        this.updateBadgeVisibilityWhenOverlappingSidenav()
      );
    }

    if (this.compRef) {
      this.compRef.instance.badgeText = status;
      this.compRef.instance.position = this.positionSignal();
      this.compRef.changeDetectorRef.markForCheck();
    }
  }

  /** Reposition overlay (e.g. after tab scroll); delegates to updateOverlayPosition. */
  private applyManualPosition(): void {
    if (this.isAttached() && this.overlayRef) {
      this.updateOverlayPosition(this.positionSignal());
    }
  }

  /**
   * Update overlay position strategy and reposition.
   */
  private updateOverlayPosition(position: 'top-right' | 'top-left'): void {
    if (!this.overlayRef) return;

    const positions: ConnectedPosition[] =
      position === 'top-right'
        ? [BADGE_POSITIONS['TOP_RIGHT']]
        : [BADGE_POSITIONS['TOP_LEFT']];

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el.nativeElement)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRef.updatePositionStrategy(strategy);

    queueMicrotask(() => {
      if (this.isAttached() && this.overlayRef) {
        this.overlayRef.updatePosition();
        requestAnimationFrame(() =>
          this.updateBadgeVisibilityWhenOverlappingSidenav()
        );
      }
    });
  }

  /**
   * Detach overlay component safely.
   */
  private detachBadge(): void {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
      this.compRef = undefined;
    }
  }

  ngOnDestroy(): void {
    this.cleanupObservers();
    this.detachBadge();
    this.overlayRef?.dispose();
    this.effectRef?.destroy();
    this.positionEffectRef?.destroy();
  }

  /**
   * Clean up all observers.
   */
  private cleanupObservers(): void {
    this.io?.disconnect();
    this.ro?.disconnect();
    this.parentRos.forEach(ro => ro.disconnect());
    this.parentRos = [];
    this.layoutWrapperMutation?.disconnect();
    this.layoutWrapperMutation = undefined;
    this.tabHeaderCleanup?.();
    this.stopPositionTracking();
  }
}
